import React, { useEffect, useState } from 'react'
import System from 'src/helper/system';
import Constants from 'src/helper/Constants';
import { profileStore } from 'src/hooks/profile';
import useReduxState from 'src/hooks/useReduxState';
import { useOrderStore } from 'src/hooks/order';
import { useAxiosIndicatorCheck } from 'src/store/hook/useAxiosIndicatorCheck';
import { __set, __remove } from "src/store/axios_indicator";
import Helper from 'src/helper/Helper';
import { useDOMAttachToSystemStore } from 'src/hooks/domAttachToSystem';
import { useCartStore } from 'src/hooks/cart';
import { Beacon } from 'src/component/Global/Beacon';
import RoleAndPermission from 'src/helper/RoleAndPermission';
import HelperAnalyticsEvent from 'src/support/HelperAnalyticsEvent';
import ShowAlert from 'src/component/Global/ShowAlert';

const CheckoutShippingMethod = () => {
    const [__check] = useAxiosIndicatorCheck();
    const { translation: t } = System;

    const { avnetCarrierType: { FEDEX, UPS, DHL },
        shippingCarrierType: { MANUAL, AVNET, FREESHIP, INCOTERM },
        checkOutSteps: { METHOD_STEP, PAYMENT_STEP },
        speedBoats: { apacAbbr, japanAbbr },
        checkoutApiSteps: { Step2 },
        siteUser: { WEB, CORE },
        salesOrgHK1E } = Constants
    const { avUserInfoData: { currentUser, speedBoatAbbr, salesOrg }, storageName: { CART_CHECKOUT } } = System

    const [exportCompliance, setExportCompliance] = useState(false)
    const [selectedAvnetCarrier, setSelectedAvnetCarrier] = useState('')
    const [isEdit, setIsEdit] = useState(false);
    const [errors, setErrors] = useState({ accountNumber: '', carrierOption: '', complianceOption: '', shipMode: '', manualCarrierOption: '' })
    const [hazardousFlag, setHazardousFlag] = useState(false)
    const [disabled, disable] = useState(false);

    const [{ all_carriers, credit_line_user, shipping_addresses, carrierErrorMessage }, { }, { set_account_address_default }] = profileStore()
    const [{ validate_order_from_server }, { }, { place_your_order }] = useOrderStore()
    const [{ CART_DATA, CART_PRODUCTS }, { }, { add_compliance_data }] = useCartStore();
    const [{ }, { system_alert_indicator, remove_system_alert_indicator }, { }] = useDOMAttachToSystemStore();

    const [checkoutInfo, setCheckoutInfo] = useReduxState({}, 'setCheckoutInfo')
    const shippingAddressInfo = checkoutInfo?.shippingAddressInfo || {}
    const shippingMethodInfo = checkoutInfo?.shippingMethodInfo || {}

    const [checkOutStepStatus, setCheckoutStepStatus] = useReduxState('', 'setCheckoutStepStatus')
    const [exportComplianceStatus, setExportComplianceStatus] = useReduxState(false, 'setExportComplianceStatus')

    const isWebUser = System.getCurrentRegionWithType(apacAbbr) && currentUser === WEB
    const isOnlySoftwareParts = System.getCurrentRegionWithType(japanAbbr) && CART_PRODUCTS && !Object.values(CART_PRODUCTS).some(f => f.SoftwareDownloadFlag === false);
    const isExportCompliancePermission = RoleAndPermission.checkActivePermission(RoleAndPermission.checkoutPermission.export_compliance)

    const freeShipObject = {
        carrierType: FREESHIP,
        value: 0,
        carrierCountry: shippingAddressInfo?.address?.countryName,
    }

    const incotermObject = {
        carrierType: INCOTERM,
        value: 0,
        carrierCountry: shippingAddressInfo?.address?.countryName,
        id: -1 // Set as static due to db and api issue for incoterm method
    }

    //  Set hazardous flag
    useEffect(() => {
        if (CART_PRODUCTS && Object.values(CART_PRODUCTS)?.length > 0) {
            const ishazardious = Object.values(CART_PRODUCTS).find(f => f.HazardousFlag === true)
            if (ishazardious) {
                setHazardousFlag(true);
                validateHazardousItemInCart()
            } else {
                setHazardousFlag(false);
            }
            if (localStorage.getItem(CART_CHECKOUT) && Object.keys(CART_DATA)?.length > 0) {
                checkoutEvent('cartCheckout');
                localStorage.removeItem(CART_CHECKOUT);
            }
        }
    }, [CART_DATA, shippingAddressInfo?.id])

    const validateHazardousItemInCart = () => {
        system_alert_indicator({
            name: 'HazardousFlag',
            message: t('hazardousItemsDesc'),
            type: "warning",
            isDisplay: false,
            isDisplayIcon: true
        })
    }

    //  Set default method for apac core user with non HK1E account
    useEffect(() => {
        if (System.getCurrentRegionWithType(apacAbbr, CORE) && salesOrg !== salesOrgHK1E) {
            setCheckoutInfo({ ...checkoutInfo, shippingMethodInfo: incotermObject })
        } else if (System.getCurrentRegionWithType(japanAbbr) && credit_line_user) {
            setCheckoutInfo({ ...checkoutInfo, shippingMethodInfo: freeShipObject })
        }
    }, [checkOutStepStatus])

    function findDefaultShipMethod() {
        const { defaultId, fedEx = [], dhl = [], ups = [], intl = [], yamato_Transport = [], lastSelectedOrderCarrierId } = all_carriers;
        const address = shipping_addresses?.find(ele => ele.id === shippingAddressInfo?.id)

        const defaultShippingMethod = address?.default === true ? defaultId : lastSelectedOrderCarrierId
        let manualCarriers = intl
        let allCarriers = [...fedEx, ...dhl, ...ups, ...yamato_Transport, ...manualCarriers];
        return allCarriers.find(carrier => carrier.id === defaultShippingMethod);
    }

    // Set default shipping method
    useEffect(() => {
        setErrors({ accountNumber: '', carrierOption: '', complianceOption: '' })
        const defaultShipMethod = findDefaultShipMethod();
        if (defaultShipMethod) {
            setCheckoutInfo({ ...checkoutInfo, shippingMethodInfo: { ...defaultShipMethod, carrierAccount: all_carriers?.carrierAccount, carrierType: defaultShipMethod?.carrierType } })
        }

        if (System.getCurrentRegionWithType(japanAbbr) && !credit_line_user && all_carriers?.yamato_Transport) {
            handleAvnetCarrierChange(all_carriers?.yamato_Transport[0])
        }

        if (carrierErrorMessage) {
            system_alert_indicator({
                name: 'carrierErrorMessage',
                message: carrierErrorMessage,
                type: "danger",
                isDisplay: false,
                isDisplayIcon: true,
            })
        } else {
            remove_system_alert_indicator('carrierErrorMessage')
        }
    }, [all_carriers])

    //  Set default shipping method for avnet carrier
    useEffect(() => {
        if (shippingMethodInfo?.carrierType === AVNET) {
            const allAvnetCarriers = [...all_carriers?.fedEx, ...all_carriers?.ups, ...all_carriers?.dhl, ...all_carriers?.yamato_Transport];

            if (shippingMethodInfo?.carrierKey) {
                setSelectedAvnetCarrier(shippingMethodInfo?.carrierKey === "FedEx" ? FEDEX : Helper.toLowerCase(shippingMethodInfo?.carrierKey));
            } else {
                setSelectedAvnetCarrier(allAvnetCarriers[0]?.carrierKey === "FedEx" ? FEDEX : Helper.toLowerCase(allAvnetCarriers[0]?.carrierKey));
            }

            if (allAvnetCarriers.length === 1) {
                setCheckoutInfo({ ...checkoutInfo, shippingMethodInfo: allAvnetCarriers[0] })
            }
        }
    }, [shippingMethodInfo?.carrierType])

    //  Check export compliance status
    useEffect(() => {
        disable(validate_order_from_server?.shippingPerference)

        if (System.getCurrentRegionWithType(japanAbbr)) {
            setExportCompliance(true)
        }
    }, [validate_order_from_server])

    //  Place order
    const ShippingMethodCallback = async (data = null) => {
        if (data) {
            await set_account_address_default(data)
        }

        const { id, ...shippingMethodWithoutId } = shippingMethodInfo;

        let params = {
            shipToId: shippingAddressInfo && shippingAddressInfo.id ? shippingAddressInfo.id : '',
            attn: shippingAddressInfo?.attn?.trim() ? shippingAddressInfo.attn?.trim() : '',
            SoftwareDownloadEmail: System.getCurrentRegionWithType(japanAbbr) && shippingAddressInfo?.SoftwareDownloadEmail?.trim() ? shippingAddressInfo.SoftwareDownloadEmail?.trim() : '',
            telephoneNumber: shippingAddressInfo?.telephoneNumber?.trim() ? shippingAddressInfo.telephoneNumber?.trim() : '',
            carrier: isOnlySoftwareParts ? freeShipObject : shippingMethodWithoutId,
            isOrderPlaced: false,
            shippingPreference: shippingAddressInfo.shippingPreference,
            speedboat: speedBoatAbbr,
            orderStep: Step2,
        }
        await place_your_order(params)

        setIsEdit(false)
        setCheckoutStepStatus(PAYMENT_STEP)
        checkoutEvent('checkoutStep2');
    }

    //  Save export compliance
    const ExportComplienceCallBack = async (data = null) => {
        let isValid = true

        if (data) {
            isValid = false
            try {
                const complianceData = await add_compliance_data(data)
                if (complianceData?.isSuccessFull) {
                    setExportComplianceStatus(true)
                }
            } catch (error) {
                console.log("error in submitting form", error)
            }
            isValid = true
        }

        if (isOnlySoftwareParts ? false : (System.getCurrentRegionWithType(apacAbbr, WEB) && all_carriers.defaultId !== shippingMethodInfo.id && isValid)) {
            System.popup("SaveShippingMethod", { shipData: shippingMethodInfo, ShippingMethodCallback })
        } else {
            ShippingMethodCallback()
        }
    }

    const handleContinue = async () => {
        let isValid = onValidateData();

        if (isValid) {
            if (System.getCurrentRegionWithType(japanAbbr) && !exportCompliance) {
                ExportComplienceCallBack();
            } else {
                if (!exportComplianceStatus && exportCompliance && System.getCurrentRegionWithType(japanAbbr) && isExportCompliancePermission) {
                    System.popup('ExportCompliance', { ExportComplienceCallBack });
                } else {

                    const address = shipping_addresses?.find(ele => ele.id === shippingAddressInfo?.id)

                    if (System.getCurrentRegionWithType(apacAbbr, WEB) && all_carriers.defaultId !== shippingMethodInfo.id && address?.default === true) {
                        System.popup("SaveShippingMethod", { shipData: shippingMethodInfo, ShippingMethodCallback })
                    } else {
                        ShippingMethodCallback()
                    }
                }
            }
        }
    }

    // Select Freeship, Manual, Avnet, Incoterm
    const handleChange = async (value) => {
        if (shippingAddressInfo?.address?.countryCode) {
            setCheckoutInfo({
                ...checkoutInfo, shippingMethodInfo: value === FREESHIP ? freeShipObject : value === INCOTERM ? incotermObject : {
                    carrierCountry: '',
                    carrierKey: '',
                    carrierOption: '',
                    carrierService: '',
                    currency: '',
                    value: 0,
                    isIntl: '',
                    carrierType: value,
                    carrierAccount: '',
                    id: ''
                }
            })
        }
    };

    // select option for MANUAL carrier change
    const handleCarrierChange = (field, field_value, options) => {
        if (field === 'carrierOption') {
            const selectedOption = options.find((item) => item.id == field_value);
            setCheckoutInfo({
                ...checkoutInfo, shippingMethodInfo: {
                    ...checkoutInfo.shippingMethodInfo,
                    carrierCountry: selectedOption?.carrierCountry || '',
                    carrierKey: selectedOption?.carrierKey,
                    carrierOption: selectedOption?.carrierOption || '',
                    carrierService: selectedOption?.carrierService || '',
                    currency: selectedOption?.currency || '',
                    value: selectedOption?.value || 0,
                    isIntl: selectedOption?.isIntl || '',
                    carrierType: selectedOption?.carrierType || MANUAL,
                    codeDescription: selectedOption?.codeDescription || '',
                    id: selectedOption?.id || ''
                }
            })
        } else {
            setCheckoutInfo({ ...checkoutInfo, shippingMethodInfo: { ...checkoutInfo.shippingMethodInfo, [field]: field_value } })
        }
    };

    // select option of avnet carriers(FEDEX,UPS,DHL)
    const handleAvnetCarrierChange = async (carrier) => {
        await setCheckoutInfo({
            ...checkoutInfo, shippingMethodInfo: {
                ...checkoutInfo.shippingMethodInfo,
                carrierCountry: carrier.carrierCountry,
                carrierKey: carrier.carrierKey,
                carrierOption: carrier.carrierOption,
                carrierService: carrier.carrierService,
                currency: carrier.currency,
                value: carrier.value,
                isIntl: carrier.isIntl,
                id: carrier.id,
                carrierType: carrier.carrierType,
                codeDescription: carrier.codeDescription,
            }
        })
    };

    // change the lable of avnet carrier
    const changeAvnetCarrier = (avnetCarrier) => {
        setSelectedAvnetCarrier(avnetCarrier)
    }

    // Account number validations
    const validateAccountNumber = (carrier, accountNumber) => {
        switch (carrier) {
            case 'UPS':
                // Length must be exactly 6 alphanumeric characters
                return Helper.validateRegex(accountNumber, /^[a-zA-Z0-9]{6}$/) ? '' : t('carrierAccountAndShipModeMismatch');
            case 'DHL':
                // Starts with 95 or 96 followed by up to 15 alphanumeric characters
                return Helper.validateRegex(accountNumber, /^(95|96)[a-zA-Z0-9]{0,13}$/) ? '' : t('carrierAccountAndShipModeMismatch');
            case 'FedEx':
                // Length must be exactly 9 digits and valid check digit
                return (accountNumber.length === 9 && Helper.validateRegex(accountNumber, /^\d{9}$/) && Helper.validateRegex(accountNumber, /^\d+$/)) ? '' : t('carrierAccountAndShipModeMismatch');
            default:
                return (accountNumber.length <= 15 && accountNumber.length !== 0) ? '' : t('carrierAccountAndShipModeMismatch');
        }
    }

    //  Validate carrier
    const onValidateData = () => {
        let validationModal = {};

        if (shippingMethodInfo?.carrierType === MANUAL) {
            if (shippingMethodInfo?.carrierOption === '' && shippingMethodInfo?.carrierAccount) {
                validationModal.manualCarrierOption = t('pleaseSelectACarrierOption');
            } else if (!shippingMethodInfo?.carrierAccount?.length) {
                validationModal.accountNumber = t('pleaseEnterACarrierAccountNumber');
            }

            let accountValidationMessage = validateAccountNumber(shippingMethodInfo?.carrierKey, shippingMethodInfo?.carrierAccount);
            if (accountValidationMessage && shippingMethodInfo?.carrierKey) {
                validationModal.shipMode = accountValidationMessage;
            }
        } else if (shippingMethodInfo.carrierType === AVNET) {
            if (Helper.toLowerCase(selectedAvnetCarrier?.trim()) === Helper.toLowerCase(shippingMethodInfo?.carrierKey?.trim())) {
                if (shippingMethodInfo.carrierOption === '') {
                    validationModal.carrierOption = t('pleaseSelectACarrierOption');
                }
            } else {
                validationModal.carrierOption = t('pleaseSelectACarrierOption');
            }
        } else if (System.getCurrentRegionWithType(apacAbbr) && !shippingMethodInfo?.carrierType) {
            validationModal.carrierOption = t('pleaseSelectACarrierOption');
        }
        if (validationModal.carrierOption) {
            system_alert_indicator({
                name: 'carrierOptionAlert',
                message: validationModal.carrierOption,
                type: "danger",
                isDisplay: false,
                isDisplayIcon: true,
            })
        } else {
            remove_system_alert_indicator('carrierOptionAlert')
        }

        setErrors(validationModal);
        return Object.keys(validationModal).length === 0;
    };

    const renderCarriers = (carriers) => {
        return carriers.map((carrier) => (
            <li key={carrier.id}>
                <div className="form-check mb-1">
                    <input
                        className="form-check-input mt-1"
                        type="radio"
                        name="avtcarrier"
                        id={`avtcarrier${carrier.id}`}
                        value={carrier.carrierOption}
                        checked={carrier.carrierOption === shippingMethodInfo?.carrierOption}
                        onChange={() => handleAvnetCarrierChange(carrier)}
                    />
                    <label className="form-check-label" htmlFor={`avtcarrier${carrier.id}`}>
                        {carrier.carrierOption}
                    </label>
                </div>
            </li>
        ));
    }
    const checkoutEvent = (event) => {
        let cartProductArr = Object.keys(CART_DATA).map((x) => {
            return { ...CART_DATA[x], ERPSupplierCode: CART_PRODUCTS[x]?.ERPSupplierCode, qas: CART_DATA[x]?.quantity }
        });
        if (cartProductArr.length > 0) {
            HelperAnalyticsEvent.cartEvent({ productList: cartProductArr, eventName: event })
        }
    }

    return (
        <div className={"shipping-address steps-inner mb-3 " +
            ((checkOutStepStatus === METHOD_STEP) ? 'active' : '') +
            ((checkOutStepStatus === PAYMENT_STEP) && !isEdit ? 'active completed' : '') +
            ((checkOutStepStatus === PAYMENT_STEP) && isEdit ? 'active' : '')}>
            <div className="checkout-titles d-flex justify-content-between"><h2>2. {t('shippingMethod')}</h2> {((checkOutStepStatus === PAYMENT_STEP)) && <a className='text-white' onClick={() => { setIsEdit(true); setCheckoutStepStatus(METHOD_STEP) }} >{t('change')} </a>}</div>
            <div className='checkbox-steps-content'>
                {carrierErrorMessage && <div className='mb-3'>
                    <ShowAlert onlyShowMessage={['carrierErrorMessage']} />
                </div>}
                <div className="step-complete-hide">
                    <div className="bs5 row">
                        {!isOnlySoftwareParts ?
                            <>
                                <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                                    <h3 className='mt-0 text-uppercase'>{t('shippingMethodOptions')}:</h3>
                                    {!carrierErrorMessage && <div>
                                        {System.getCurrentRegionWithType(apacAbbr) &&
                                            <>
                                                {!isWebUser && <div className="form-check mb-2">
                                                    <input className="form-check-input mt-1" type="radio" name="incoterm"
                                                        id="incoterm"
                                                        value={INCOTERM}
                                                        checked={shippingMethodInfo.carrierType === INCOTERM}
                                                        onChange={() => handleChange(INCOTERM)} />
                                                    <label className="form-check-label" htmlFor="incoterm">
                                                        {t('asPerIncoterm')}
                                                    </label>
                                                </div>}
                                                {(salesOrg === salesOrgHK1E || isWebUser) && <div className='mycarrieraccount'>
                                                    <div className="form-check mb-2">
                                                        <input className="form-check-input mt-1" type="radio" name="shippingMethod" id="MANUAL"
                                                            value={MANUAL}
                                                            checked={shippingMethodInfo.carrierType === MANUAL}
                                                            onChange={() => handleChange(MANUAL)} />
                                                        <label className="form-check-label" htmlFor="MANUAL">
                                                            {t('useMyCarrierAccount')}
                                                        </label>
                                                    </div>

                                                    {shippingMethodInfo.carrierType === MANUAL && <div className='ms-3 ps-1'>
                                                        <div className="mb-2">
                                                            <label htmlFor="accountNumber" className="form-label" >{t('carrierAccountNumber')}*</label>
                                                            <input type="text" className="form-control" id="accountNumber" placeholder={t('carrierAccountNumber')} value={shippingMethodInfo?.carrierAccount} onChange={(e) => handleCarrierChange('carrierAccount', e.target.value)} />
                                                            {errors.accountNumber && <p className="error">{errors.accountNumber}</p>}
                                                        </div>
                                                        <div className="mb-2">
                                                            <div className="mb-2">
                                                                <label htmlFor="carrierMethod" className="form-label">{t('selectCarrierMethod')}*</label>
                                                                <div>
                                                                    <select className="form-control" id="carrierMethod" value={shippingMethodInfo?.id} onChange={(e) => {
                                                                        handleCarrierChange('carrierOption', e.target.value, all_carriers?.intl);
                                                                    }}>
                                                                        <option value="" defaultValue>- {t('pleaseSelect')} -</option>
                                                                        {all_carriers?.intl?.map((carrier) => (
                                                                            <option key={carrier.id} value={carrier.id}>{carrier.carrierOption}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                {errors.manualCarrierOption && <p className="error">{errors.manualCarrierOption}</p>}
                                                                {errors.shipMode && <p className="error">{errors.shipMode}</p>}
                                                            </div>

                                                        </div>
                                                    </div>}
                                                </div>}
                                                {isWebUser && <div className='avnetcarrier mb-3'>
                                                    <div className="form-check mb-2">
                                                        <input className="form-check-input mt-1" type="radio" name="shippingMethod"
                                                            id="AVNET"
                                                            value={AVNET}
                                                            checked={shippingMethodInfo.carrierType === AVNET}
                                                            onChange={() => handleChange(AVNET)} />
                                                        <label className="form-check-label" htmlFor="AVNET">
                                                            {t('useAvnetsCarrierOptions')}
                                                        </label>
                                                    </div>
                                                    {shippingMethodInfo.carrierType === AVNET && <div className='avnetcarrierselection'>
                                                        <div className='d-flex'>
                                                            {all_carriers?.fedEx?.length > 0 && <button
                                                                className={`bs5 btn ${selectedAvnetCarrier === FEDEX ? "btn-primary" : "btn-secondary"} me-2`}
                                                                onClick={() => changeAvnetCarrier(FEDEX)}
                                                            >
                                                                Fedex
                                                            </button>}
                                                            {all_carriers?.dhl?.length > 0 && <button
                                                                className={`bs5 btn ${selectedAvnetCarrier === DHL ? "btn-primary" : "btn-secondary"} me-2`}
                                                                onClick={() => changeAvnetCarrier(DHL)}
                                                            >
                                                                Dhl
                                                            </button>}
                                                            {all_carriers?.ups?.length > 0 && <button
                                                                className={`bs5 btn ${selectedAvnetCarrier === UPS ? "btn-primary" : "btn-secondary"}`}
                                                                onClick={() => changeAvnetCarrier(UPS)}
                                                            >
                                                                Ups
                                                            </button>}
                                                        </div>

                                                        <ul className='mx-0 px-0 mt-3'>
                                                            {selectedAvnetCarrier === FEDEX && renderCarriers(all_carriers.fedEx)}
                                                            {selectedAvnetCarrier === UPS && renderCarriers(all_carriers.ups)}
                                                            {selectedAvnetCarrier === DHL && renderCarriers(all_carriers.dhl)}
                                                        </ul>
                                                    </div>}
                                                </div>}
                                            </>}

                                        {System.getCurrentRegionWithType(japanAbbr) &&
                                            <>
                                                {!credit_line_user ?
                                                    <div className='avnetcarrier mb-3'>
                                                        <div className="form-check mb-2">
                                                            <input className="form-check-input mt-1" type="radio" name="yomato"
                                                                id="yomato"
                                                                value={all_carriers.yamato_Transport}
                                                                checked={true}
                                                                onChange={() => handleAvnetCarrierChange(all_carriers.yamato_Transport)} />
                                                            <label className="form-check-label" htmlFor="yomato">
                                                                {all_carriers?.yamato_Transport && all_carriers?.yamato_Transport[0]?.carrierOption}
                                                            </label>
                                                        </div>
                                                    </div>
                                                    : <div className="form-check mb-2">
                                                        <input className="form-check-input mt-1" type="radio" name="defaultShippingCompany"
                                                            id="defaultShippingCompany"
                                                            value={FREESHIP}
                                                            checked={true}
                                                            onChange={() => handleChange(FREESHIP)} />
                                                        <label className="form-check-label" htmlFor="defaultShippingCompany">
                                                            {t('defaultShippingCompany')}
                                                        </label>
                                                    </div>}
                                            </>}
                                    </div>}
                                    <hr></hr>
                                    <div className="mb-3">
                                        <h3 className="mt-0">{t('shippingPreferences')}:</h3>
                                        <p className='font-13 font-arial mb-0'><strong> {t('itemsWillBeShippedWhenTheyBecomeAvailable')}</strong></p>
                                        {System.getCurrentRegionWithType(apacAbbr) && <p className='mt-0'>{t('additionalFreightChargesMultipleShip')}.</p>}

                                        <div className="form-check mb-1 mt-3">
                                            <input disabled={disabled} className="form-check-input mt-1 " type="checkbox"
                                                name="shipre" id="shipre1"
                                                checked={System.getCurrentRegionWithType(japanAbbr) ? false : shippingAddressInfo.shippingPreference}
                                                onChange={(event) => setCheckoutInfo({ ...checkoutInfo, shippingAddressInfo: { ...checkoutInfo.shippingAddressInfo, shippingPreference: event.target.checked } })} />

                                            <label className="form-check-label" htmlFor="shipre1">
                                                <b>
                                                    {t('shipOnlyWhenAllItemsBecomeAvailable')}
                                                </b>
                                            </label>
                                            <br />
                                            <i>({t('backorderItemsMayDelayOrder')})</i>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                                    <div className="shipping-additional-info step-complete-hide info-text">
                                        <p>{System.getCurrentRegionWithType(japanAbbr) ? t('shippingMethodStaticTextJapan') : t('shippingMethodStaticTextApac')}</p>
                                    </div>
                                </div>
                            </> :
                            <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6"><b>{t('orderContainsOnlySoftwareItems')}</b></div>}

                        <div className="col-12 text-end mt-4 step-complete-hide">
                            <button type="submit"
                                disabled={carrierErrorMessage}
                                className="bs5 btn btn-primary btn-sm w130"
                                onClick={handleContinue}>{t('continue')}</button>
                        </div>
                    </div>
                </div>

                <div className="step-complete-show">
                    {isOnlySoftwareParts ? <div className="mb-3"><b>{t('orderContainsOnlySoftwareItems')}</b></div> : <><div className="mb-3">
                        <h3 className="mt-0 text-uppercase">{t('shippingMethodOptions')}:</h3>
                        {shippingMethodInfo?.carrierType === 'FreeShip' ?
                            <p className='font-13 font-arial'><strong> {t('defaultShippingCompany')}</strong></p> :
                            shippingMethodInfo?.carrierType === INCOTERM ?
                                <p className='font-13 font-arial'><strong> {t('asPerIncoterm')}</strong></p> :
                                <div>
                                    <span className='font-13 font-arial me-5'><strong> {shippingMethodInfo?.carrierKey}</strong></span>
                                    <span className='font-13 font-arial'>
                                        {shippingMethodInfo?.carrierType === AVNET && System.getCurrentRegionWithType(japanAbbr) ? "¥600" :
                                            shippingMethodInfo?.carrierType === MANUAL ? shippingMethodInfo?.carrierAccount :
                                                shippingMethodInfo?.carrierOption}</span>
                                </div>
                        }
                    </div>
                        <hr></hr>
                        <div className="mb-3">
                            <h3 className="mt-0">{t('shippingPreferences')}:</h3>
                            {shippingAddressInfo.shippingPreference ?
                                <p className='font-13 font-arial'>{t('combineAllItemsIntoOneShipment')}</p>
                                :
                                <>
                                    <p className='font-13 font-arial'><strong> {t('shipItemsAsTheyBecomeAvailable')}</strong></p>
                                    {System.getCurrentRegionWithType(apacAbbr) && <p className='mt-0'>({t('additionalFreightChargesMultipleShip')})</p>}
                                </>
                            }
                        </div>
                    </>}
                </div>
            </div>
        </div>
    );
}

export default CheckoutShippingMethod;